import { SEM_CONTA_PATH } from './config';
import { logIdp } from './log';
import { clearIdpSession } from './login-flow';
import { clearIdpTokens, getIdpAccessToken } from './token-store';

/**
 * O que fazer quando a API do Sapron recusa (401) uma requisicao que saiu com
 * o token do IDP.
 *
 * A API responde `{"detail": "IDP token rejected: <motivo>"}` (backend,
 * `authentication/backends/idp_jwks.py`, `_fail`). O motivo separa dois casos:
 *
 * - **Sem vinculo** (`user_not_found`, `inactive_user`, `ambiguous_email`): o
 *   token e bom, mas a conta nao abre nada no Sapron. Mandar para o
 *   `/login#expired` deixava a SSO do Keycloak viva e o proximo "Entrar com
 *   Seazone" voltava aqui sem pedir credencial, em laco. Recebe o mesmo
 *   tratamento do sem-vinculo do callback: limpa a sessao, faz o logout
 *   federado e mostra a `/sem-conta`. Quem faz isso e o `IdpAuthProvider`.
 * - **Qualquer outro** (`expired`, `invalid_token`, `unknown_kid`,
 *   `jwks_unavailable`, ou 401 sem motivo legivel): o problema e o token, nao
 *   a conta. Nao e logout: descarta so o token em memoria, mantem a marca de
 *   sessao da aba e recarrega no `/login`, onde o boot refaz o login sem tela
 *   (`prompt=none`). Sem sessao no Keycloak, a pessoa ve o login de sempre.
 *
 * O interceptor roda fora do React e nao alcanca o contexto; por isso o
 * provider registra aqui o tratador do sem-vinculo ao montar.
 */

const MOTIVOS_SEM_VINCULO = new Set(['user_not_found', 'inactive_user', 'ambiguous_email']);
const PREFIXO_DO_DETAIL = 'IDP token rejected: ';

/**
 * Guarda contra laco no caminho do token recusado: se a API recusar de novo o
 * token novo pouco depois da re-autenticacao (chave do IDP trocada, JWKS fora
 * do ar), a segunda volta ao `/login` vai sem a marca e para na tela de login.
 */
const REAUTENTICOU_KEY = 'sapron-idp-reautenticou-em';
const JANELA_DO_LACO_MS = 2 * 60 * 1000;

type Tratador = () => void;

let tratador: Tratador | undefined;

/** O motivo da recusa, lido do corpo do 401; `undefined` quando nao ha. */
export function motivoDaRecusa(corpo: unknown): string | undefined {
  if (corpo === null || typeof corpo !== 'object') return undefined;
  const { code, detail } = corpo as { code?: unknown; detail?: unknown };
  if (typeof code === 'string' && code !== '') return code;
  if (typeof detail === 'string' && detail.startsWith(PREFIXO_DO_DETAIL)) {
    return detail.slice(PREFIXO_DO_DETAIL.length).trim() || undefined;
  }
  return undefined;
}

export function ehSemVinculo(motivo: string | undefined): boolean {
  return motivo !== undefined && MOTIVOS_SEM_VINCULO.has(motivo);
}

/** Registrado pelo `IdpAuthProvider`. Devolve a funcao que desfaz o registro. */
export function registrarRecusaDaApi(novo: Tratador): () => void {
  tratador = novo;
  return () => {
    if (tratador === novo) tratador = undefined;
  };
}

function semVinculo(): void {
  if (tratador !== undefined) {
    tratador();
    return;
  }
  // Sem provider montado (nao acontece no app): o minimo seguro fora do React.
  clearIdpTokens();
  clearIdpSession();
  window.location.href = SEM_CONTA_PATH;
}

function lerStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

function tokenRecusado(motivo: string | undefined): void {
  const storage = lerStorage();
  const ultima = Number(storage?.getItem(REAUTENTICOU_KEY) ?? 0);
  const emLaco = ultima > 0 && Date.now() - ultima < JANELA_DO_LACO_MS;

  if (emLaco) {
    storage?.removeItem(REAUTENTICOU_KEY);
    clearIdpSession();
    logIdp('idp_login.failed', { reason: 'api_recusou_token_de_novo', motivo });
  } else {
    storage?.setItem(REAUTENTICOU_KEY, String(Date.now()));
    logIdp('idp_login.failed', { reason: 'api_recusou_token', motivo });
  }

  clearIdpTokens();
  window.location.href = '/login';
}

/**
 * Chamado pelo interceptor no 401 de uma requisicao feita com a sessao do IDP,
 * com o corpo da resposta e o access token com que ela saiu.
 *
 * So age se esse token ainda e o vigente. Uma requisicao que saiu com o token
 * A e volta 401 depois de o refresh instalar o B (notebook que acorda com o A
 * vencido) fala de uma sessao que ja nao existe; agir derrubaria o B, que e
 * valido. O mesmo filtro cobre varios 401 juntos: o primeiro limpa o token, e
 * os outros ja nao encontram o deles.
 */
export function avisarRecusaDaApi(corpo: unknown, tokenUsado: string): void {
  if (getIdpAccessToken() !== tokenUsado) return;

  const motivo = motivoDaRecusa(corpo);
  if (ehSemVinculo(motivo)) {
    semVinculo();
    return;
  }
  tokenRecusado(motivo);
}
